import { tz } from "@date-fns/tz";
import {
  CircularProgress,
  Switch,
  FormControlLabel,
  Box,
  Grid2,
  Icon,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { addHours, format } from "date-fns";
import * as React from "react";
import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { TableVirtuoso, TableComponents } from "react-virtuoso";

import { ChartDateType } from "api";
import {
  GetRecordsResponse,
  EnergyType,
  HelperFunctionsChartRecords,
} from "api/queries/records/types";
import { Widget } from "components/elements/mui";
import { useFormatMessage } from "lang/context/utils";
import { getSelectedPowerPlant, useAppSelector } from "store";
import { formatEnergy } from "utils/energy";

interface RecordsTableProps {
  data: GetRecordsResponse<EnergyType> | undefined;
  isLoading: boolean;
  dateType: ChartDateType;
  energyType: EnergyType;
  helperFunctions: HelperFunctionsChartRecords<EnergyType>;
}

// In the day view the rows are hours that unfold into the meter's own
// records (one per save, ~5 min); other views list their periods flat.
type RowKind = "flat" | "hour" | "record";

interface TableRowData {
  key: string;
  kind: RowKind;
  expanded?: boolean;
  dateTime: string;
  energyConsumed: string;
  energyProduced: string;
  inductiveEnergyConsumed: string;
  capacitiveEnergyProduced: string;
  inductiveEnergyProduced: string;
  capacitiveEnergyConsumed: string;
}

interface TableContext {
  toggle: (key: string) => void;
}

const VirtuosoTableComponents: TableComponents<TableRowData, TableContext> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
    <TableContainer component={"div"} {...props} ref={ref} />
  )),
  Table: (props) => <Table {...props} sx={{ borderCollapse: "separate" }} />,
  TableRow: ({ item, context, ...props }) => (
    <TableRow
      {...props}
      hover={item.kind === "hour"}
      onClick={item.kind === "hour" ? () => (context as TableContext | undefined)?.toggle(item.key) : undefined}
      sx={item.kind === "hour" ? { cursor: "pointer" } : undefined}
    />
  ),
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableBody {...props} ref={ref} />
  )),
};

const fixedHeaderContent = (energyType: EnergyType) => {
  return (
    <TableRow
      sx={{
        backgroundColor: "background.paper",
      }}
    >
      <TableCell
        variant="head"
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 2,
        }}
      >
        <FormattedMessage id="date" />
      </TableCell>
      {[energyType].map((type) => (
        <TableCell key={type} variant="head" sx={{ minWidth: "max-content" }}>
          <FormattedMessage id={type} />
        </TableCell>
      ))}
    </TableRow>
  );
};

const rowContent = (
  _index: number,
  row: TableRowData,
  energyType: EnergyType,
) => {
  const child = row.kind === "record";
  const bg = child ? "action.hover" : "background.paper";
  return (
    <>
      <TableCell
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 1,
          borderRight: 1,
          borderColor: "divider",
          backgroundColor: bg,
          fontWeight: row.kind === "hour" ? 600 : undefined,
          ...(child ? { color: "text.secondary", fontSize: "0.8rem", py: 0.5, pl: 5 } : {}),
        }}
      >
        {row.kind === "hour" ? (
          <Stack direction="row" alignItems="center" gap={0.25}>
            <Icon
              sx={{
                fontSize: 16,
                color: "text.secondary",
                transition: "transform 150ms",
                transform: row.expanded ? "rotate(90deg)" : "none",
              }}
            >
              chevron_right
            </Icon>
            {row.dateTime}
          </Stack>
        ) : (
          row.dateTime
        )}
      </TableCell>
      {[energyType].map((type) => (
        <TableCell
          key={type}
          sx={{
            backgroundColor: bg,
            fontWeight: row.kind === "hour" ? 600 : undefined,
            ...(child ? { color: "text.secondary", fontSize: "0.8rem", py: 0.5 } : {}),
          }}
        >
          {row[type]}
        </TableCell>
      ))}
    </>
  );
};

export const RecordsTable: React.FC<RecordsTableProps> = ({
  data,
  isLoading,
  dateType,
  energyType,
  helperFunctions,
}) => {
  const formatMessage = useFormatMessage();
  const theme = useTheme();
  const [showRawData, setShowRawData] = useState(false);

  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const timezone = tz(selectedPowerPlant?.timezone || "Europe/Warsaw");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const tableData = useMemo((): TableRowData[] => {
    if (!data) return [];
    type Rec = (typeof data)[number];
    const energyTypes = [
      "energyConsumed",
      "energyProduced",
      "inductiveEnergyConsumed",
      "capacitiveEnergyProduced",
      "inductiveEnergyProduced",
      "capacitiveEnergyConsumed",
    ] as const;
    // The meter reading at the end of the interval, or the energy in it.
    const valueOf = (record: Rec, type: EnergyType) =>
      showRawData ? record[type].raw + record[type].calculated : record[type].calculated;
    const cells = (pick: (type: EnergyType) => number) =>
      Object.fromEntries(
        energyTypes.map((type) => [type, formatEnergy(pick(type), showRawData).formatted]),
      ) as Record<EnergyType, string>;
    const recordRow = (record: Rec, kind: RowKind): TableRowData => ({
      key: record.date,
      kind,
      dateTime: helperFunctions[dateType].formatXTooltip(new Date(record.date), record.period),
      ...cells((type) => valueOf(record, type)),
    });

    if (dateType !== "day") return data.map((record) => recordRow(record, "flat"));

    // Hours of the plant's day, each unfolding into its records.
    const groups = new Map<string, Rec[]>();
    for (const record of data) {
      const key = format(new Date(record.date), "yyyy-MM-dd HH", { in: timezone });
      groups.set(key, [...(groups.get(key) ?? []), record]);
    }
    const rows: TableRowData[] = [];
    for (const [key, records] of groups) {
      const hourStart = new Date(records[0].date);
      const isOpen = expanded.has(key);
      const last = records[records.length - 1];
      rows.push({
        key,
        kind: "hour",
        expanded: isOpen,
        dateTime: `${format(hourStart, "HH:00", { in: timezone })} - ${format(addHours(hourStart, 1), "HH:00", { in: timezone })}`,
        // Meter readings: the reading at the end of the hour; energy: the hour's sum.
        ...cells((type) =>
          showRawData ? valueOf(last, type) : records.reduce((a, r) => a + r[type].calculated, 0),
        ),
      });
      if (isOpen) for (const record of records) rows.push(recordRow(record, "record"));
    }
    return rows;
  }, [data, dateType, helperFunctions, showRawData, timezone, expanded]);

  return (
    <Widget
      title={formatMessage("table")}
      icon="table_chart"
      size={{ xs: 12 }}
      expandable
      onlyHeaderClickable
      content={
        !tableData.length || isLoading ? (
          <div
            style={{
              height: 200,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {isLoading ? (
              <CircularProgress />
            ) : (
              <p style={{ color: theme.palette.text.disabled }}>
                {formatMessage("chart.noData")}
              </p>
            )}
          </div>
        ) : (
          <Grid2
            container
            direction="column"
            flexWrap="nowrap"
            sx={{
              minHeight: 400,
              height: "60dvh",
              width: "100%",
            }}
          >
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showRawData}
                    onChange={(e) => setShowRawData(e.target.checked)}
                    color="secondary"
                  />
                }
                label={formatMessage("table.meterReadings")}
              />
            </Box>
            <TableVirtuoso
              data={tableData}
              context={{ toggle }}
              computeItemKey={(_index, row) => row.key}
              components={VirtuosoTableComponents}
              fixedHeaderContent={() => fixedHeaderContent(energyType)}
              itemContent={(index, data) => rowContent(index, data, energyType)}
            />
          </Grid2>
        )
      }
    />
  );
};
